import { RESPONSIVE_PADDING, SCREEN_WIDTH } from 'constants/ui';

export const drawerStyles = {
  zIndex: 999,
};

export const paperStyles = {
  width: 680,
  padding: RESPONSIVE_PADDING.DESKTOP,
  backgroundColor: ({ palette }) => palette.background.disabled,

  [`@media(max-width: ${SCREEN_WIDTH.M - 1}px)`]: {
    width: '100%',
    padding: RESPONSIVE_PADDING.MOBILE,
    // The Drawer paper is portaled to <body>, so the layout's safe-area inset doesn't reach it. On a
    // native notched device its header (the only close button) would sit under the Dynamic Island and
    // be untappable — inset the top by the safe area. env() is 0 on web/desktop, so this is a no-op
    // off-device.
    paddingTop: `calc(${RESPONSIVE_PADDING.MOBILE} + env(safe-area-inset-top, 0px))`,
  },

  [`@media(min-width: ${SCREEN_WIDTH.M}px) and (max-width: ${SCREEN_WIDTH.L - 1}px)`]: {
    width: 'calc(100% - 100px)',
    padding: RESPONSIVE_PADDING.TABLET,
  },

  [`@media(min-width: ${SCREEN_WIDTH.XL}px)`]: {
    width: 820,
  },
};

export const contentWrapperStyles = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

export const headerStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
};

export const closeButtonStyles = {
  minWidth: 'auto',
  padding: '4px',
};

export const stickyToolbarStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '16px',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: ({ palette }) => palette.background.disabled,
  paddingTop: '8px',
  paddingBottom: '8px',
};

export const searchInputStyles = {
  flex: 1,
  '& .MuiOutlinedInput-root': {
    backgroundColor: ({ palette }) => palette.background.paper,
    borderRadius: '8px',
    fontSize: '13px',
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 12px',
  },
};

export const toggleAllButtonStyles = {
  fontSize: '12px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  minWidth: 'auto',
};

export const metricsGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '8px',
  marginBottom: '16px',
  paddingBottom: '16px',
  borderBottom: ({ palette }) => `1px solid ${palette.border.default}`,
};

export const metricCardStyles = {
  backgroundColor: ({ palette }) => palette.background.paper,
  borderRadius: '8px',
  padding: '12px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
};

export const metricIconStyles = {
  display: 'flex',
  color: ({ palette }) => palette.primary.main,
};

export const metricLabelStyles = {
  fontSize: '11px',
  color: ({ palette }) => palette.text.secondary,
  marginBottom: '4px',
};

export const metricValueStyles = {
  fontSize: '16px',
  fontWeight: 600,
};

export const bodyStyles = {
  flex: 1,
  overflowY: 'auto',
};

export const loaderWrapperStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '200px',
};

export const progressStyles = {
  color: ({ palette }) => palette.primary.main,
};

export const emptyStateStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '200px',
  color: ({ palette }) => palette.text.secondary,
};

export const sectionStyles = {
  marginBottom: '8px',
  backgroundColor: ({ palette }) => palette.background.paper,
  borderRadius: '8px',
  overflow: 'hidden',
};

export const sectionIconStyles = {
  display: 'flex',
  color: ({ palette }) => palette.primary.main,
};

export const sectionHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: ({ palette }) => palette.background.disabled,
  },
};

export const sectionTitleRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

export const sectionCountBadgeStyles = {
  fontSize: '11px',
  color: ({ palette }) => palette.text.secondary,
  backgroundColor: ({ palette }) => palette.background.disabled,
  borderRadius: '10px',
  padding: '1px 8px',
  lineHeight: '18px',
};

export const sectionContentStyles = {
  padding: '0 16px 16px',
};

export const fieldRowStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '6px 8px',
  borderBottom: ({ palette }) => `1px solid ${palette.border.default}`,
  borderRadius: '4px',
  '&:nth-of-type(even)': {
    backgroundColor: ({ palette }) => palette.background.secondary,
  },
  '&:last-child': {
    borderBottom: 'none',
  },
};

export const fieldLabelStyles = {
  color: ({ palette }) => palette.text.secondary,
  fontSize: '13px',
  flexShrink: 0,
  marginRight: '12px',
};

export const fieldValueStyles = {
  fontSize: '13px',
  fontWeight: 500,
  textAlign: 'right',
  wordBreak: 'break-word',
};

export const arrayItemStyles = {
  backgroundColor: ({ palette }) => palette.background.disabled,
  borderRadius: '6px',
  padding: '12px',
  marginBottom: '8px',
  '&:last-child': {
    marginBottom: 0,
  },
};

export const fieldValueCopyWrapperStyles = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

export const copyIconStyles = {
  display: 'flex',
  color: ({ palette }) => palette.text.disabled,
  opacity: 0,
  transition: 'opacity 0.2s',
  '.MuiBox-root:hover &': {
    opacity: 1,
  },
};

export const highlightStyles = {
  backgroundColor: ({ palette }) => palette.background.tableHighlight,
  borderRadius: '2px',
  padding: '0 1px',
};

export const noResultsStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '32px 16px',
  color: ({ palette }) => palette.text.secondary,
};
